import { useState } from 'react'
import { api, query } from '../api/client'
import { useQuery } from '../api/hooks'
import type { Party, PartyType } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import { Empty, ErrorBanner, Field, Loading, Modal, Panel, Tag } from '../components/ui'

const TYPES: PartyType[] = ['Client', 'Vendor', 'Employee', 'Authority']

/**
 * Party master. Creating one generates its sub-ledger accounts automatically — a client gets
 * three balances, not one, and an employee gets custody, loan and advance separately, because the
 * three settle differently (BUILD_PLAN §3.2).
 */
export function PartiesPage() {
  const { can } = useAuth()
  const [type, setType] = useState<PartyType>('Client')
  const [term, setTerm] = useState('')
  const [editing, setEditing] = useState<Party | null>(null)
  const [creating, setCreating] = useState(false)

  const parties = useQuery<Party[]>(`/parties${query({ type, term })}`, [type, term])

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>Type</label>
          <select value={type} onChange={(e) => setType(e.target.value as PartyType)}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Search</label>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Name, code or VAT registration"
            style={{ minWidth: 260 }}
          />
        </div>

        <div className="spacer" />

        {can(P.parties.create) && (
          <button className="primary" onClick={() => setCreating(true)}>
            + New {type.toLowerCase()}
          </button>
        )}
      </div>

      <ErrorBanner error={parties.error} />
      {parties.loading && <Loading />}

      {parties.data && (
        <Panel flush>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Code</th>
                  <th>Name</th>
                  <th style={{ width: 200 }}>العربية</th>
                  <th style={{ width: 140 }}>VAT reg.</th>
                  <th style={{ width: 160 }}>Contact</th>
                  <th style={{ width: 120 }}>Sub-ledgers</th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {parties.data.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <Empty>No {type.toLowerCase()}s match.</Empty>
                    </td>
                  </tr>
                )}

                {parties.data.map((party) => (
                  <tr key={party.id} style={{ opacity: party.isActive ? 1 : 0.5 }}>
                    <td className="code">{party.code}</td>
                    <td>{party.nameEn}</td>
                    <td dir="rtl" className="soft">
                      {party.nameAr}
                    </td>
                    <td className="code">{party.vatRegistrationNo}</td>
                    <td className="soft">{party.phone ?? party.email}</td>
                    <td>
                      {party.controlAccountId && <Tag kind="info">main</Tag>}{' '}
                      {party.custodyAccountId && <Tag kind="warn">custody</Tag>}{' '}
                      {party.settlementAccountId && <Tag kind="warn">settle</Tag>}{' '}
                      {party.loanAccountId && <Tag>loan</Tag>}
                    </td>
                    <td>
                      {can(P.parties.edit) && (
                        <button className="subtle small" onClick={() => setEditing(party)}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {(creating || editing) && (
        <PartyForm
          type={type}
          party={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            parties.reload()
          }}
        />
      )}
    </>
  )
}

function PartyForm({
  type,
  party,
  onClose,
  onSaved,
}: {
  type: PartyType
  party: Party | null
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    nameEn: party?.nameEn ?? '',
    nameAr: party?.nameAr ?? '',
    vatRegistrationNo: party?.vatRegistrationNo ?? '',
    taxId: party?.taxId ?? '',
    phone: party?.phone ?? '',
    email: party?.email ?? '',
    address: party?.address ?? '',
    paymentTermDays: party?.paymentTermDays?.toString() ?? '',
    withholdingTaxRate: party?.withholdingTaxRate?.toString() ?? '',
    usesTrustAccount: party?.usesTrustAccount ?? false,
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }))
  }

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      const body = {
        type: party?.type ?? type,
        nameEn: form.nameEn.trim(),
        nameAr: form.nameAr.trim(),
        vatRegistrationNo: form.vatRegistrationNo.trim() || null,
        taxId: form.taxId.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        paymentTermDays: form.paymentTermDays ? Number(form.paymentTermDays) : null,
        withholdingTaxRate: form.withholdingTaxRate ? Number(form.withholdingTaxRate) : null,
        usesTrustAccount: form.usesTrustAccount,
      }

      if (party) {
        await api(`/parties/${party.id}`, { method: 'PUT', body })
      } else {
        await api('/parties', { method: 'POST', body })
      }

      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  const effectiveType = party?.type ?? type

  return (
    <Modal
      title={party ? `Edit ${party.code} — ${party.nameEn}` : `New ${type.toLowerCase()}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !form.nameEn} onClick={() => void save()}>
            {busy ? 'Saving…' : party ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      {!party && (
        <div className="banner info">
          The sub-ledger accounts are created automatically.{' '}
          {effectiveType === 'Client'
            ? 'A client gets three: fees receivable, custody held, and settlements held.'
            : effectiveType === 'Employee'
              ? 'An employee gets three: custody, business loan, and salary advance.'
              : 'A control account is created under the right branch.'}
        </div>
      )}

      <div className="grid cols-2">
        <Field label="Name (English)">
          <input value={form.nameEn} onChange={(e) => set('nameEn', e.target.value)} />
        </Field>

        <Field label="Name (Arabic)">
          <input dir="rtl" value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} />
        </Field>

        <Field
          label="VAT registration"
          hint="The document keys customers by this. Two parties of the same type cannot share one."
        >
          <input
            value={form.vatRegistrationNo}
            onChange={(e) => set('vatRegistrationNo', e.target.value)}
          />
        </Field>

        <Field label="Tax ID">
          <input value={form.taxId} onChange={(e) => set('taxId', e.target.value)} />
        </Field>

        <Field label="Phone">
          <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
        </Field>

        <Field label="Email">
          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
        </Field>

        <Field label="Payment terms (days)">
          <input
            className="num"
            inputMode="numeric"
            value={form.paymentTermDays}
            onChange={(e) => set('paymentTermDays', e.target.value)}
          />
        </Field>

        {effectiveType === 'Vendor' && (
          <Field label="WHT rate" hint="As a decimal, e.g. 0.05 for 5%.">
            <input
              className="num"
              inputMode="decimal"
              value={form.withholdingTaxRate}
              onChange={(e) => set('withholdingTaxRate', e.target.value)}
            />
          </Field>
        )}
      </div>

      <Field label="Address">
        <input value={form.address} onChange={(e) => set('address', e.target.value)} />
      </Field>

      {effectiveType === 'Client' && (
        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={form.usesTrustAccount}
            onChange={(e) => set('usesTrustAccount', e.target.checked)}
          />
          This client&apos;s money is held in a segregated trust account
        </label>
      )}
    </Modal>
  )
}
